import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, FormHelperText, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { useToast } from '../../context/ToastContext';
import { PosTerminal, PosTerminalRegisterResponse, PosTerminalStatus } from '../../interfaces/pos.interface';
import { Store } from '../../interfaces/store.interface';
import { formatDateTime } from '../../utils/dateUtils';

// Status -> Chip color. REVOKED reads as an ended state, not an error; PENDING_ACTIVATION is the
// normal "waiting for the counter to type the activation code in" state, not a warning either.
const STATUS_COLORS: Record<PosTerminalStatus, 'success' | 'default' | 'warning'> = {
    ACTIVE: 'success',
    PENDING_ACTIVATION: 'warning',
    REVOKED: 'default',
};

// NOT a clock-skew field -- see PosTerminal.lastPushLagSeconds in pos.interface.ts. Only a
// negative value (a sale timestamped in the server's future) is unambiguous evidence of a wrong
// counter clock; a positive value is ordinary offline-queue latency and must never be labelled
// "clock skew".
const renderLag = (lagSeconds: number | null) => {
    if (lagSeconds === null) return <span>—</span>;
    if (lagSeconds < 0) {
        return (
            <Tooltip title="This terminal reported a sale timestamped in the server's future. Its clock appears to be ahead, which shifts the sale into the wrong day on every report.">
                <Chip color="warning" size="small" label={`Clock appears to be ahead by ${Math.abs(lagSeconds)}s`} />
            </Tooltip>
        );
    }
    return (
        <Tooltip title="Time between the sale being rung and the server receiving it. A large value usually means the terminal was offline for a while — it is not by itself evidence of a wrong clock.">
            <span>{`Sync lag ${lagSeconds}s`}</span>
        </Tooltip>
    );
};

const STORE_PAGE_SIZE = 100;

const emptyRegisterForm = { terminalCode: '', label: '' };

const PosTerminalList: React.FC = () => {
    const toast = useToast();

    const [stores, setStores] = useState<Store[]>([]);
    // F8 (task-3 review): true when the store list was cut off by the picker's page-size cap
    // (below), so the manager sees a signal that a store might be missing rather than silently
    // concluding it does not exist.
    const [storeListTruncated, setStoreListTruncated] = useState(false);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [terminals, setTerminals] = useState<PosTerminal[]>([]);
    const [loadingTerminals, setLoadingTerminals] = useState(false);
    // Distinguishes "the store genuinely has no terminals" from "the load failed" (final-review
    // F1, same defect as PosCashierAccess.tsx's F1 -- see the comment at PosCashierAccess.tsx:97).
    // The stakes here are not merely cosmetic: a manager who reads a failed load as "no terminals
    // registered" may register a duplicate, or revoke a terminal they should not have.
    const [terminalsLoadError, setTerminalsLoadError] = useState(false);

    const [registerOpen, setRegisterOpen] = useState(false);
    const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
    const [registerError, setRegisterError] = useState('');
    const [registering, setRegistering] = useState(false);

    // Set only after a successful register -- drives the once-only activation-code dialog. Its
    // absence (null) is what closes that dialog; there is no separate "activation dialog open"
    // flag to fall out of sync with it.
    const [registered, setRegistered] = useState<PosTerminalRegisterResponse | null>(null);

    const [revokeTarget, setRevokeTarget] = useState<PosTerminal | null>(null);
    const [revoking, setRevoking] = useState(false);

    const [renameTarget, setRenameTarget] = useState<PosTerminal | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);

    useEffect(() => {
        loadStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStores = async () => {
        try {
            // Capped at STORE_PAGE_SIZE -- deliberately, not an oversight (F8, task-3 review): no
            // institution running this POS has approached that count yet. An institution that
            // does will need this picker paginated or made searchable; storeListTruncated is the
            // honest interim signal that some stores are not shown, rather than none at all.
            const response = await getStoresByInstitutionId(0, STORE_PAGE_SIZE);
            const content = response.content ?? [];
            setStores(content);
            setStoreListTruncated((response.totalElements ?? content.length) > content.length);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
        }
    };

    const loadTerminals = async (storeId: string) => {
        try {
            setLoadingTerminals(true);
            setTerminalsLoadError(false);
            const response = await PosService.listTerminals(storeId);
            setTerminals(response);
        } catch (err) {
            setTerminalsLoadError(true);
            toast.showApiError(err, 'Failed to load terminals');
        } finally {
            setLoadingTerminals(false);
        }
    };

    const handleStoreChange = (event: SelectChangeEvent) => {
        const storeId = event.target.value;
        setSelectedStoreId(storeId);
        setTerminals([]);
        setTerminalsLoadError(false);
        if (storeId) {
            loadTerminals(storeId);
        }
    };

    const openRegisterDialog = () => {
        setRegisterForm(emptyRegisterForm);
        setRegisterError('');
        setRegisterOpen(true);
    };

    const closeRegisterDialog = () => {
        setRegisterOpen(false);
        setRegisterForm(emptyRegisterForm);
        setRegisterError('');
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setRegisterError('');
        try {
            setRegistering(true);
            const response = await PosService.registerTerminal(
                selectedStoreId, registerForm.terminalCode.trim(), registerForm.label.trim(),
            );
            closeRegisterDialog();
            setRegistered(response);
            await loadTerminals(selectedStoreId);
        } catch (err: any) {
            // Bare-string 400s, no structured field-error body -- same substring-match precedent
            // as ItemList.tsx's barcode handler.
            const apiMessage = err?.response?.data;
            if (typeof apiMessage === 'string' && apiMessage.toLowerCase().includes('already exists')) {
                setRegisterError(apiMessage);
            } else {
                toast.showApiError(err, 'Failed to register terminal');
            }
        } finally {
            setRegistering(false);
        }
    };

    const closeActivationDialog = () => setRegistered(null);

    const openRevokeConfirm = (terminal: PosTerminal) => setRevokeTarget(terminal);
    const closeRevokeConfirm = () => setRevokeTarget(null);

    const handleConfirmRevoke = async () => {
        if (!revokeTarget) return;
        try {
            setRevoking(true);
            await PosService.revokeTerminal(revokeTarget.id);
            toast.showSuccess('Terminal revoked');
            closeRevokeConfirm();
            await loadTerminals(selectedStoreId);
        } catch (err) {
            toast.showApiError(err, 'Failed to revoke terminal');
        } finally {
            setRevoking(false);
        }
    };

    const openRenameDialog = (terminal: PosTerminal) => {
        setRenameTarget(terminal);
        setRenameValue(terminal.label ?? '');
    };
    const closeRenameDialog = () => {
        setRenameTarget(null);
        setRenameValue('');
    };

    const handleRenameSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!renameTarget) return;
        try {
            setRenaming(true);
            await PosService.renameTerminal(renameTarget.id, renameValue.trim());
            closeRenameDialog();
            await loadTerminals(selectedStoreId);
        } catch (err) {
            toast.showApiError(err, 'Failed to rename terminal');
        } finally {
            setRenaming(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Terminals</Typography>
                <Button
                    variant="contained"
                    disabled={!selectedStoreId}
                    onClick={openRegisterDialog}
                >
                    Register Terminal
                </Button>
            </Box>

            <FormControl sx={{ minWidth: 280, mb: 3 }}>
                <InputLabel id="pos-terminal-store-label">Store</InputLabel>
                <Select
                    labelId="pos-terminal-store-label"
                    label="Store"
                    value={selectedStoreId}
                    onChange={handleStoreChange}
                >
                    {stores.map(store => (
                        <MenuItem key={store.id} value={store.id}>{store.storeName}</MenuItem>
                    ))}
                </Select>
                {storeListTruncated && (
                    <FormHelperText>
                        Showing the first {STORE_PAGE_SIZE} stores. If a store is missing, it did
                        not fit this list — contact support rather than assuming it does not exist.
                    </FormHelperText>
                )}
            </FormControl>

            {!selectedStoreId && (
                <Alert severity="info">Choose a store to see its terminals.</Alert>
            )}

            {selectedStoreId && loadingTerminals && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            )}

            {selectedStoreId && !loadingTerminals && terminalsLoadError && (
                <Alert
                    severity="error"
                    action={(
                        <Button color="inherit" size="small" onClick={() => loadTerminals(selectedStoreId)}>
                            Retry
                        </Button>
                    )}
                >
                    Failed to load terminals for this store. This is NOT the same as having no
                    terminals registered -- try again before registering a duplicate or revoking
                    the wrong one.
                </Alert>
            )}

            {selectedStoreId && !loadingTerminals && !terminalsLoadError && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Label</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last Seen</TableCell>
                                <TableCell>Sync Lag</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {terminals.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography color="text.secondary">
                                            No terminals registered for this store yet.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {terminals.map(terminal => (
                                <TableRow key={terminal.id}>
                                    <TableCell>{terminal.terminalCode}</TableCell>
                                    <TableCell>{terminal.label || '—'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={STATUS_COLORS[terminal.status]}
                                            label={terminal.status}
                                        />
                                    </TableCell>
                                    <TableCell>{terminal.lastSeenAt ? formatDateTime(terminal.lastSeenAt) : '—'}</TableCell>
                                    <TableCell>{renderLag(terminal.lastPushLagSeconds)}</TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() => openRenameDialog(terminal)}
                                            disabled={terminal.status === 'REVOKED'}
                                        >
                                            Rename
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() => openRevokeConfirm(terminal)}
                                            disabled={terminal.status === 'REVOKED'}
                                        >
                                            Revoke
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Register dialog */}
            <Dialog open={registerOpen} onClose={closeRegisterDialog} fullWidth maxWidth="xs">
                <form onSubmit={handleRegisterSubmit}>
                    <DialogTitle>Register Terminal</DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            label="Terminal Code"
                            value={registerForm.terminalCode}
                            onChange={e => {
                                setRegisterError('');
                                setRegisterForm(prev => ({ ...prev, terminalCode: e.target.value }));
                            }}
                            error={Boolean(registerError)}
                            helperText={registerError}
                            required
                            autoFocus
                            fullWidth
                        />
                        <TextField
                            label="Label"
                            value={registerForm.label}
                            onChange={e => setRegisterForm(prev => ({ ...prev, label: e.target.value }))}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeRegisterDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={registering}>Register</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Activation code dialog -- shown exactly once, on the response of a successful register.
                No endpoint can ever return this code again: if the manager closes this without
                copying it, the only recovery is to revoke this terminal and register a new one
                UNDER A DIFFERENT CODE -- register's duplicate-code check is not scoped to
                non-revoked terminals (carried debt, README), so the revoked row still owns this
                terminal code and re-using it would 400. See also F3/F1 below: this dialog cannot
                be dismissed except by its own button, because the value is truly unrecoverable. */}
            <Dialog open={Boolean(registered)} onClose={undefined} disableEscapeKeyDown fullWidth maxWidth="xs">
                <DialogTitle>Terminal Registered</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        This activation code is shown only once and cannot be retrieved again. Copy it
                        into the counter app now. If it is lost, revoke this terminal and register a
                        new one under a different code — this terminal code cannot be reused once
                        revoked.
                    </Alert>
                    <Typography variant="h5" sx={{ fontFamily: 'monospace', mt: 2 }}>
                        {registered?.activationCode}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={closeActivationDialog}>I have copied it — Done</Button>
                </DialogActions>
            </Dialog>

            {/* Revoke confirmation */}
            <Dialog open={Boolean(revokeTarget)} onClose={closeRevokeConfirm} fullWidth maxWidth="xs">
                <DialogTitle>Revoke Terminal?</DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Revoking is permanent. There is no way to reactivate this terminal or recover
                        an activation code for it again.
                    </Alert>
                    <Typography>
                        This terminal keeps working until its current access token expires (up to 15
                        minutes) or its next contact, whichever is sooner — revoking does not stop it
                        instantly. Any sales still queued on the device when it stops contacting the
                        server <strong>cannot be recovered afterwards</strong>. If this terminal has
                        unsynced sales, let it sync first.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRevokeConfirm}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleConfirmRevoke} disabled={revoking}>
                        Confirm Revoke
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rename dialog */}
            <Dialog open={Boolean(renameTarget)} onClose={closeRenameDialog} fullWidth maxWidth="xs">
                <form onSubmit={handleRenameSubmit}>
                    <DialogTitle>Rename Terminal</DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                        <TextField
                            label="Label"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            autoFocus
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeRenameDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" disabled={renaming}>Save</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default PosTerminalList;
